import { useEffect, useRef, useState, useCallback } from 'react';

const WS_URL = process.env.REACT_APP_WS_URL;
const API_URL = process.env.REACT_APP_API_URL;

export const useWebSocket = () => {
  const ws = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [streamStats, setStreamStats] = useState({});
  const [streamUpdates, setStreamUpdates] = useState({});
  const reconnectTimeout = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  const intentionalClose = useRef(false);
  const socketGeneration = useRef(0);

  const getWebSocketUrl = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const sameOriginWs = `${protocol}//${window.location.host}/ws`;

    // In production, prefer same-origin websocket path through nginx proxy.
    if (process.env.NODE_ENV === 'production') {
      if (WS_URL && /^wss?:\/\//.test(WS_URL)) {
        try {
          const configured = new URL(WS_URL);
          if (configured.host === window.location.host) {
            console.log('[WebSocket] Using same-host REACT_APP_WS_URL:', WS_URL);
            return WS_URL;
          }
          console.warn('[WebSocket] Ignoring cross-host REACT_APP_WS_URL in production:', WS_URL);
        } catch (e) {
          console.warn('[WebSocket] Invalid REACT_APP_WS_URL, falling back to same-origin:', WS_URL);
        }
      }

      console.log('[WebSocket] Using production same-origin URL:', sameOriginWs);
      return sameOriginWs;
    }

    // Development / non-production behavior:
    if (WS_URL && /^wss?:\/\//.test(WS_URL)) {
      console.log('[WebSocket] Using REACT_APP_WS_URL:', WS_URL);
      return WS_URL;
    }

    if (API_URL) {
      if (API_URL.startsWith('/')) {
        console.log('[WebSocket] Derived URL from relative API:', sameOriginWs);
        return sameOriginWs;
      }

      const wsProtocol = API_URL.startsWith('https:') ? 'wss:' : 'ws:';
      const baseUrl = API_URL
        .replace(/^https?:/, wsProtocol)
        .replace(/\/api\/?$/, '')
        .replace(/\/$/, '');
      const wsUrl = `${baseUrl}/ws`;

      if (wsUrl.startsWith('ws://') || wsUrl.startsWith('wss://')) {
        console.log('[WebSocket] Derived URL from API:', wsUrl);
        return wsUrl;
      }
    }

    console.log('[WebSocket] Using fallback URL:', sameOriginWs);
    return sameOriginWs;
  }, []);

  const connect = useCallback(() => {
    try {
      // Prevent multiple simultaneous connections
      if (ws.current && (ws.current.readyState === WebSocket.CONNECTING || ws.current.readyState === WebSocket.OPEN)) {
        console.log('[WebSocket] Connection already in progress or established');
        return;
      }

      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }

      const wsUrl = getWebSocketUrl();
      
      console.log('[WebSocket] Connecting to:', wsUrl);
      const currentGeneration = ++socketGeneration.current;
      const socket = new WebSocket(wsUrl);
      ws.current = socket;

      socket.onopen = () => {
        if (currentGeneration !== socketGeneration.current) return;
        console.log('[WebSocket] Connected successfully');
        setIsConnected(true);
        reconnectAttempts.current = 0;

        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
          ws.current.send(JSON.stringify({ type: 'subscribe_all' }));
        }
      };

      socket.onmessage = (event) => {
        if (currentGeneration !== socketGeneration.current) return;
        try {
          const data = JSON.parse(event.data);
          handleMessage(data);
        } catch (error) {
          console.error('[WebSocket] Error parsing message:', error);
        }
      };

      socket.onclose = (event) => {
        if (currentGeneration !== socketGeneration.current) return;

        console.log(
          '[WebSocket] Disconnected:',
          {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
            url: wsUrl,
            pageOrigin: window.location.origin
          }
        );
        setIsConnected(false);

        if (ws.current === socket) {
          ws.current = null;
        }
        
        // Attempt reconnection with exponential backoff + jitter
        if (!intentionalClose.current && reconnectAttempts.current < maxReconnectAttempts) {
          if (reconnectTimeout.current) {
            clearTimeout(reconnectTimeout.current);
            reconnectTimeout.current = null;
          }

          const baseDelay = Math.min(5000 * Math.pow(2, reconnectAttempts.current), 60000);
          const jitter = Math.floor(Math.random() * 1000);
          const delay = baseDelay + jitter;
          reconnectAttempts.current++;
          
          console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current}/${maxReconnectAttempts})`);
          reconnectTimeout.current = setTimeout(() => {
            connect();
          }, delay);
        } else if (!intentionalClose.current) {
          console.error('[WebSocket] Max reconnection attempts reached');
        }
      };

      socket.onerror = (error) => {
        if (currentGeneration !== socketGeneration.current) return;
        console.error('[WebSocket] Error:', {
          error,
          url: wsUrl,
          pageOrigin: window.location.origin,
          readyState: socket.readyState
        });
        setIsConnected(false);
      };
    } catch (error) {
      console.error('[WebSocket] Error creating connection:', error);
      setIsConnected(false);
    }
  }, [getWebSocketUrl]);

  const handleMessage = useCallback((data) => {
    switch (data.type) {
      case 'connected':
        console.log('[WebSocket] Server says:', data.message);
        break;
        
      case 'stream_stats':
        // Global stats update
        if (data.data) {
          setStreamStats(data.data);
        }
        break;
        
      case 'stream_update':
        // Individual stream update
        if (data.streamKey) {
          setStreamUpdates(prev => ({
            ...prev,
            [data.streamKey]: {
              ...data.data,
              timestamp: data.timestamp
            }
          }));
        }
        break;
        
      case 'stream_status_change':
        // Stream went live or offline
        console.log(`[WebSocket] Stream ${data.streamKey} is now ${data.isLive ? 'LIVE' : 'OFFLINE'}`);
        break;
        
      case 'pong':
        // Heartbeat response
        break;
        
      case 'subscribed':
        // Subscription confirmation
        console.log('[WebSocket] Subscribed:', data.message || data.streams);
        break;
        
      default:
        console.log('[WebSocket] Unknown message type:', data.type);
    }
  }, []);

  const sendMessage = useCallback((message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
    } else {
      console.warn('[WebSocket] Cannot send message - not connected');
    }
  }, []);

  const subscribeToStreams = useCallback((streamIds) => {
    sendMessage({
      type: 'subscribe',
      streamIds: streamIds
    });
  }, [sendMessage]);

  useEffect(() => {
    intentionalClose.current = false;
    connect();
    
    // Heartbeat to keep connection alive
    const heartbeat = setInterval(() => {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        sendMessage({ type: 'ping' });
      }
    }, 30000);

    return () => {
      clearInterval(heartbeat);
      intentionalClose.current = true;
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [connect, sendMessage]);

  return {
    isConnected,
    streamStats,
    streamUpdates,
    subscribeToStreams,
    sendMessage
  };
};

export default useWebSocket;
